// Phase-0 spike — S1 probes (browser-direct CORS + auth).
//
// The decisive Phase-0 question: with ONLY the user's key and NO server, can the
// browser reach each provider's token endpoint and Anthropic directly?
//
// Reading the result:
//   - fetch REJECTS (TypeError)        -> CORS blocked (or offline). The endpoint
//                                         cannot be called browser-direct.
//   - fetch RESOLVES with any status   -> CORS is ALLOWED. 2xx = full success;
//                                         401/403 = CORS fine, key/request wrong.
// So `corsAllowed` (did we reach the server at all?) is the architectural signal;
// `authOk` is secondary.

import type { ProbeResult } from './types'

const SPEECHMATICS_MP = 'https://mp.speechmatics.com/v1/api_keys?type=rt'
const SONIOX_TMP_KEY = 'https://api.soniox.com/v1/auth/temporary-api-key'
const ANTHROPIC_MESSAGES = 'https://api.anthropic.com/v1/messages'

type JsonRecord = Record<string, unknown>

function parseJson(text: string): JsonRecord | null {
  try {
    return JSON.parse(text) as JsonRecord
  } catch {
    return null
  }
}

/** Run a fetch and classify it as CORS-blocked vs reached-the-server. */
async function probe(
  url: string,
  init: RequestInit,
  tokenField?: string
): Promise<ProbeResult> {
  let res: Response
  try {
    res = await fetch(url, init)
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    return {
      corsAllowed: false,
      authOk: false,
      detail: `fetch rejected (${msg}) — CORS blocked or offline. This endpoint is not browser-direct callable.`
    }
  }

  const body = await res.text()
  const json = parseJson(body)
  const token =
    tokenField && json && typeof json[tokenField] === 'string'
      ? (json[tokenField] as string)
      : undefined

  return {
    corsAllowed: true,
    authOk: res.ok,
    status: res.status,
    token,
    detail: res.ok
      ? `CORS allowed · HTTP ${res.status} · ${token ? 'token received' : 'ok'}`
      : `CORS allowed · HTTP ${res.status} · ${body.slice(0, 200)}`
  }
}

export function probeSpeechmatics(apiKey: string): Promise<ProbeResult> {
  return probe(
    SPEECHMATICS_MP,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`
      },
      body: JSON.stringify({ ttl: 3600 })
    },
    'key_value'
  )
}

export function probeSoniox(apiKey: string): Promise<ProbeResult> {
  return probe(
    SONIOX_TMP_KEY,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        usage_type: 'transcribe_websocket',
        expires_in_seconds: 3600
      })
    },
    'api_key'
  )
}

/**
 * Anthropic browser-direct: the SDK sets `anthropic-dangerous-direct-browser-
 * access: true` to opt into CORS. We send it on a raw fetch to test the same
 * path. Expected to PASS — the interesting providers are the STT ones above.
 */
export function probeAnthropic(apiKey: string): Promise<ProbeResult> {
  return probe(ANTHROPIC_MESSAGES, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true'
    },
    body: JSON.stringify({
      model: 'claude-haiku-4-5',
      max_tokens: 16,
      messages: [{ role: 'user', content: 'Reply with the single word: pong' }]
    })
  })
}
